import React, { Component } from 'react'
import propTypes from 'prop-types'
import { withRouter } from 'react-router'
import { inject } from 'mobx-react'
import { Formik, Form, Field } from 'formik'
import yup from 'yup'

import { employeeApi, officeApi } from '@atheneum/api'

import Button from '@components/Button'
import WarningIcon from '@components/Icon/Warning'
import InfoBox from '@components/InfoBox'
import { Autocomplete } from '@components/Form/Autocomplete'
import InfoMessage from './FormInfoMessage'
import pick from 'lodash/pick'

import AddressFields from '@components/Form/AddressFields'
import Input from '@components/Form/Input'

class OfficeForm extends Component {
  static propTypes = {
    office: propTypes.object,
    account: propTypes.object,
    parentAccount: propTypes.object,
    modalStore: propTypes.object,
    onSaveSuccess: propTypes.func,
    staticData: propTypes.object
  }

  static defaultProps = {
    hideButtons: true,
    showCloseIcon: true,
    shouldCloseOnOverlayClick: false
  }

  constructor(props) {
    super(props)
    this.state = {
      error: null,
      employeeOptions: []
    }

    this.loadEmployeeOptions()
  }

  saveOffice = officeData => {
    if (!this.props.office) {
      officeData.officeName = `${officeData.officeName} ${officeData.address.city}`
      return officeApi.create(officeData)
    }
    return officeApi.update(this.props.office, officeData)
  }

  getInitialValues = () => {
    const { office, parentAccount, account } = this.props
    return office
      ? pick(office, ['parentAccountId', 'accountId', 'officeName', 'address', 'keyContactId'])
      : {
          parentAccountId: parentAccount.id,
          accountId: account.id,
          keyContactId: null,
          officeName: parentAccount.parentAccountName,
          address: {
            address1: '',
            address2: '',
            city: '',
            postalCode: '',
            state: '',
            countryId: null,
            timezoneId: null
          }
        }
  }

  getValidationSchema = () => {
    return yup.object().shape({
      officeName: yup
        .string()
        .max(128, 'Office name can be no longer than 128 characters')
        .required('Office name is required'),
      address: yup.object().shape({
        city: yup.string().required('City field is required')
      }),
      keyContactId: yup.number().integer().nullable(),
    })
  }

  loadEmployeeOptions = async () => {
    const { EMPLOYEE_POSITIONS } = this.props.staticData.CONST
    const employees = await employeeApi.getByPosition([
      EMPLOYEE_POSITIONS.ACCOUNT_MANAGER,
      EMPLOYEE_POSITIONS.PRINCIPAL
    ])

    this.setState({ employeeOptions: employees })
  }

  handleKeyContactSelection = (employee, form) => {
    if (employee) {
      form.setFieldValue('keyContactId', employee.id, false)
    } else {
      form.setFieldValue('keyContactId', null, false)
    }
  }

  render () {
    return (
      <div>
        <Formik
          initialValues={this.getInitialValues()}
          validationSchema={this.getValidationSchema()}
          onSubmit={(values, actions) => {
            this.saveOffice(values)
              .then(this.props.onSaveSuccess)
              .catch(({ message }) => {
                this.setState({ error: message })
                actions.setSubmitting(false)
              })
          }}
          render={form => (
            <div className='expert-form'>
              <h1 className='expert-form__title'>
                {this.props.office ? 'Edit office details' : 'Create office'}
              </h1>
              {this.state.error ? (
                <InfoBox theme='red' icon={<WarningIcon />}>
                  {this.state.error}
                </InfoBox>
              ) : null}
              <Form onSubmit={form.handleSubmit}>
                <div className='expert-form__section'>
                  {!this.props.office && (
                    <>
                      <div className='expert-form__input-group m-t-md'>
                        <label className='expert-form__label'>
                          Display name
                        </label>
                        <div
                          className='expert-form__value-wrapper'
                          style={{ lineHeight: 2.2, fontSize: '16px' }}
                        >
                          {`${form.values.officeName} ${
                            form.values.address ? form.values.address.city : ''
                          }`}
                        </div>
                      </div>
                      <InfoMessage
                        margin='m-t-0'
                        message='Created automatically from office name and city'
                      />
                    </>
                  )}
                  <Field
                    name='officeName'
                    label='Office name'
                    component={Input}
                  />
                  {!this.props.office && (
                    <InfoMessage
                      margin='m-t-md'
                      message='Do not add the city name, it will be added automatically'
                    />
                  )}
                  <Field
                    valueIsId
                    name="keyContactId"
                    label="Key Atheneum contact"
                    placeholder="Select employee"
                    itemToString={item => item && item.fullName}
                    options={this.state.employeeOptions}
                    onChange={value =>
                      this.handleKeyContactSelection(value, form)
                    }
                    component={Autocomplete}
                  />
                  <AddressFields
                    data-cy='addressfields-autocomplete'
                    form={form}
                    name='address'
                    cityLabel='City / Postal code'
                    timezone
                    editAddress
                    secondaryAddress
                    postalCode
                  />
                </div>
                <div className='expert-form__section expert-form__section--buttons'>
                  <Button
                    primary
                    submit
                    onClick={this.onSubmit}
                    disabled={form.isSubmitting}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={this.props.modalStore.close}
                    disabled={form.isSubmitting}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          )}
        />
      </div>
    )
  }
}

export default withRouter(inject('modalStore', 'staticData')(OfficeForm))
